import { useEffect, useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Repeat2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox, selectableRowClass } from '@/components/ui/range-select';
import { applyToSet, useRangeSelection } from '@/hooks/useRangeSelection';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckboxField, Note, Segmented } from '../workspace/ui';
import { useDiscoveryClient } from '../api/context';
import { useCapabilities } from '../hooks/capabilities';
import type { Folder, RepostAnalysis, RepostCandidate, RepostRunResult } from '../api/types';

/**
 * Find creators through the reposts of the ones already in this folder.
 *
 * Two steps, like the lookalike flow and for the same reason: analysing costs
 * one API call per seed, and running costs enrichment for everyone chosen. The
 * picker sits between them so nothing is searched on a guess.
 *
 * ── What the list is, and isn't ────────────────────────────────────────────
 * Measured over 20 seeds on two real folders, only 2 of ~230 candidates were
 * reposted by more than one creator. So this is NOT "creators similar to your
 * folder" — it's "creators your folder amplifies", which is a real but
 * different claim, and the copy says so. `seedCount` is still shown because
 * when two seeds do agree that's rare and worth seeing; the ordering is
 * carried in practice by how often your creators reposted someone.
 */

const compact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${Math.round(n / 1_000)}k` : String(n);

export function RepostDialog({
  folder,
  onClose,
  onStarted,
}: {
  folder: Folder;
  onClose: () => void;
  onStarted: (result: RepostRunResult) => void;
}): JSX.Element {
  const client = useDiscoveryClient();
  const [analysis, setAnalysis] = useState<RepostAnalysis | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createFolder, setCreateFolder] = useState(true);
  const [folderName, setFolderName] = useState(`${folder.name} — Reposts`);
  const [excludeOfficial, setExcludeOfficial] = useState(true);
  const [presenterFilter, setPresenterFilter] = useState<'any' | 'faces' | 'faceless'>('any');
  const presenterCheckAvailable = useCapabilities().presenterCheck;
  // Applied server-side against the captions of the videos that were actually
  // reposted. A folder's creators repost whatever they enjoyed, which is wider
  // than what the folder is for.
  const [topic, setTopic] = useState('');

  const analyzeMutation = useMutation({
    mutationFn: (terms: string[] = []) =>
      client.folders.analyzeReposts(folder.publicId, { maxSeeds: 15, topicTerms: terms }),
    onSuccess: (data) => {
      setAnalysis(data);
      setSelected(new Set(data.suggested));
    },
  });

  const runMutation = useMutation({
    mutationFn: () =>
      client.folders.runReposts(folder.publicId, {
        usernames: [...selected],
        createFolder,
        folderName: createFolder ? folderName : undefined,
        excludeOfficial,
        ...(presenterFilter !== 'any' && presenterCheckAvailable ? { presenterFilter } : {}),
      }),
    onSuccess: onStarted,
  });

  // Analyse on open: the dialog exists to show this, and making someone click
  // "analyse" first is a step with no decision in it.
  const { mutate: analyze } = analyzeMutation;
  useEffect(() => { analyze([]); }, [analyze]);

  const range = useRangeSelection({
    visible: (analysis?.candidates ?? []).map((c) => c.username),
    isSelected: (username) => selected.has(username),
    onApply: applyToSet(setSelected),
  });

  const excludedCounts = (analysis?.excluded ?? []).reduce<Record<string, number>>((acc, e) => {
    acc[e.reason] = (acc[e.reason] ?? 0) + 1;
    return acc;
  }, {});

  const presenterValue = presenterCheckAvailable ? presenterFilter : 'any';
  const reanalyze = () => analyzeMutation.mutate(topic.split(',').map((t) => t.trim()).filter(Boolean));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-2xl sm:p-0">
        <DialogHeader className="border-b border-border px-5 py-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <Repeat2 className="h-4 w-4 text-brand" strokeWidth={2} />
            Creators reposted by “{folder.name}”
          </DialogTitle>
          <DialogDescription>Accounts this folder's TikTok creators amplify. Pick who to add.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {analyzeMutation.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading reposts from this folder's creators…
            </p>
          )}
          {analyzeMutation.error && <Note tone="error">{(analyzeMutation.error as Error).message}</Note>}

          {analysis && (
            <>
              <p className="text-sm text-muted-foreground">
                {analysis.seedsUsed.length} creators from this folder reposted {analysis.repostsScanned} videos, turning up{' '}
                <strong className="text-foreground">{analysis.candidates.length}</strong> creators you don't already have.
                These are accounts your folder amplifies — not necessarily similar creators, so worth a look before you run
                them.
                {Object.keys(excludedCounts).length > 0 && (
                  <>
                    {' '}Hidden: {excludedCounts.known ?? 0} already yours, {excludedCounts.self ?? 0} alt accounts,{' '}
                    {excludedCounts.viral ?? 0} one-off viral reposts
                    {excludedCounts.offTopic ? `, ${excludedCounts.offTopic} off-topic` : ''}.
                  </>
                )}
              </p>

              <div className="flex items-end gap-2">
                <label className="grid flex-1 gap-1">
                  <span className="text-sm font-medium">Only creators whose reposts are about…</span>
                  <Input
                    placeholder="love island, reality tv"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        reanalyze();
                      }
                    }}
                  />
                </label>
                <Button
                  variant="outline"
                  disabled={analyzeMutation.isPending}
                  onClick={reanalyze}
                  title="Re-reads the reposts — one API call per creator in the folder"
                >
                  Apply
                </Button>
              </div>

              <div className="grid gap-1">
                <span className="text-sm font-medium">On camera</span>
                <Segmented
                  label="On camera"
                  value={presenterValue}
                  onChange={(v) => presenterCheckAvailable && setPresenterFilter(v)}
                  options={[
                    { value: 'any', label: 'Anyone' },
                    { value: 'faces', label: 'Face creators' },
                    { value: 'faceless', label: 'Faceless' },
                  ]}
                  className={cn('w-fit', !presenterCheckAvailable && 'pointer-events-none opacity-50')}
                />
                {!presenterCheckAvailable && <span className="text-sm text-muted-foreground">Not configured on this server.</span>}
              </div>

              {analysis.candidates.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2.5 px-3 text-sm font-medium">
                    <SelectAllCheckbox label="Select every creator" state={range.headState} onToggle={range.toggleAll} />
                    Select all
                  </label>
                  <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                  <span className="text-sm text-muted-foreground">{selected.size} selected</span>
                  <RangeSelectHint show={selected.size > 0} />
                </div>
              )}

              <ul className="divide-y divide-border rounded-lg border border-border">
                {analysis.candidates.map((c: RepostCandidate) => (
                  <li
                    key={c.username}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2 transition-colors',
                      selected.has(c.username) ? 'bg-brand/10' : 'hover:bg-accent/40',
                      selectableRowClass(),
                    )}
                    {...range.rowProps(c.username)}
                  >
                    <RowCheckbox aria-label={`Add @${c.username}`} className="rounded border-input" {...range.checkboxProps(c.username)} />
                    <a
                      href={`https://www.tiktok.com/@${c.username}`}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 flex-1 truncate text-sm hover:underline"
                      title={`Reposted by ${c.viaSeeds.join(', ')}`}
                    >
                      @{c.username}
                      {c.nickname && <span className="ml-1.5 text-muted-foreground">{c.nickname}</span>}
                    </a>
                    {c.seedCount > 1 && (
                      <span className="shrink-0 rounded-md border border-brand/40 bg-brand/10 px-1.5 py-0.5 text-xs font-medium text-brand">
                        {c.seedCount} agree
                      </span>
                    )}
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {c.repostCount}× · {compact(c.topPlays)} plays
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <footer className="space-y-3 border-t border-border px-5 py-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <CheckboxField checked={createFolder} onChange={setCreateFolder}>
              Put them in a new folder
            </CheckboxField>
            {createFolder && (
              <Input aria-label="New folder name" className="max-w-[16rem]" value={folderName} onChange={(e) => setFolderName(e.target.value)} />
            )}
            <CheckboxField checked={excludeOfficial} onChange={setExcludeOfficial}>
              Skip official and gossip accounts
            </CheckboxField>
          </div>
          {runMutation.error && <Note tone="error">{(runMutation.error as Error).message}</Note>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={selected.size === 0 || runMutation.isPending} onClick={() => runMutation.mutate()}>
              {runMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {runMutation.isPending ? 'Starting…' : `Add ${selected.size}`}
            </Button>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
